import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';

import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.ip.management.member_content');
import { normalizeWebsite } from "src/libs/portal/season/contact-policy";
import { OnDestroy, OnInit } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Service } from "src/libs/portal/season/service";
import { Subscription } from 'rxjs';

type ContentCategory = 'notices' | 'laws' | 'press' | 'forms';

type ContentItem = {
    id: string;
    title: string;
    summary: string;
    status: '임시저장' | '공개';
    isPinned: boolean;
    updatedAt: string;
    linkUrl: string;
    linkLabel: string;
    attachments: any[];
    version: number;
    createdAt?: string;
};

@Component({
    selector: 'wiz-page-ip-management-member_content',
template: templateSource || '',
    styles: [`

/* file: /mnt/data/wiz/project/main/build/src/app/page.ip.management.member_content/view.scss */
:host {
  display: block;
  min-height: 100%;
  background: transparent;
}

.ipm-content-page :is(button, input, textarea, select, a):focus-visible {
  outline: 2px solid #10b981;
  outline-offset: 2px;
}`],
})
export class PageIpManagementMember_contentComponent implements OnInit, OnDestroy {
    public category: ContentCategory = 'notices';
    public categories: { key: ContentCategory; label: string; description: string }[] = [
        { key: 'notices', label: '공지사항', description: '서비스 운영 및 이용 안내' },
        { key: 'laws', label: '법령/기준', description: '관련 법령과 심사·업무 기준' },
        { key: 'press', label: '언론보도', description: '보도자료 및 주요 언론 기사' },
        { key: 'forms', label: '각종 양식', description: '신청서·동의서·업무 서식' }
    ];
    public content: { [key: string]: ContentItem[] } = {
        notices: [],
        laws: [],
        press: [],
        forms: []
    };
    public selected: ContentItem | null = null;
    public draft: ContentItem = this.emptyContent();
    public editorOpen = false;
    public editing = false;
    public saving = false;
    public loading = true;
    public loadError = '';
    public notice = '';
    public search = '';
    public statusFilter = '';
    public sort: 'latest' | 'oldest' | 'title' = 'latest';
    public page: number = 1;
    public readonly pageSize: number = 10;
    public pendingFiles: File[] = [];
    public uploadProgress = 0;
    private routeSubscription: Subscription = null;
    private readonly maxFiles = 10;
    private readonly maxFileSize = 20 * 1024 * 1024;
    private readonly allowedExtensions = [
        '.pdf', '.hwp', '.hwpx', '.doc', '.docx', '.xls', '.xlsx',
        '.ppt', '.pptx', '.txt', '.csv', '.zip', '.jpg', '.jpeg', '.png'
    ];

    constructor(@Inject( Service) public service: Service,@Inject( Router)  private router: Router) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        await this.loadContent();
        this.syncFromLocation();
        this.routeSubscription = this.router.events.subscribe(async (event) => {
            if (!(event instanceof NavigationEnd)) return;
            this.syncFromLocation();
            await this.service.render();
            document.querySelector('.wb-route-stage')?.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
        });
        await this.service.render();
    }

    public ngOnDestroy() {
        this.routeSubscription?.unsubscribe();
    }

    private responseData(response: any) {
        const payload = response && response.responseJSON ? response.responseJSON : response;
        const status = Number(payload?.code ?? response?.status ?? 500);
        const data = payload?.data;
        if (!payload || status >= 400) {
            const message = payload?.message
                || (typeof data === 'string' ? data : data?.message)
                || response?.statusText
                || '요청을 처리하지 못했습니다.';
            throw new Error(String(message));
        }
        return data;
    }

    public async loadContent() {
        this.loading = true;
        this.loadError = '';
        try {
            const data: any = this.responseData(await wiz.call('list_content'));
            this.categories.forEach((category) => {
                this.content[category.key] = Array.isArray(data?.[category.key]) ? data[category.key] : [];
            });
        } catch (error) {
            this.loadError = String(error?.message || '콘텐츠를 불러오지 못했습니다.');
        } finally {
            this.loading = false;
        }
    }

    private syncFromLocation() {
        const params = new URLSearchParams(location.search);
        const requestedCategory = params.get('category') as ContentCategory;
        const allowed = this.categories.some((item) => item.key === requestedCategory);
        this.category = allowed ? requestedCategory : 'notices';
        const itemId = params.get('item') || '';
        this.selected = this.items.find((item) => item.id === itemId) || null;
        this.editorOpen = itemId === 'new' || !!this.selected;
        this.editing = itemId === 'new';
        this.pendingFiles = [];
        this.uploadProgress = 0;
        this.notice = itemId && itemId !== 'new' && !this.selected ? '해당 콘텐츠를 찾을 수 없습니다.' : '';
        this.draft = this.selected ? this.cloneContent(this.selected) : this.emptyContent();
    }

    public get activeCategory() {
        return this.categories.find((item) => item.key === this.category) || this.categories[0];
    }

    public get items() {
        return this.content[this.category] || [];
    }

    public get publicCount() {
        return this.items.filter((item) => item.status === '공개').length;
    }

    public get pinnedCount() {
        return this.items.filter((item) => item.status === '공개' && item.isPinned).length;
    }

    public get hasFilters() {
        return !!this.search.trim() || !!this.statusFilter || this.sort !== 'latest';
    }

    public get supportsAttachments() {
        return false;
    }

    public get filteredItems() {
        const query = this.search.trim().toLowerCase();
        const rows = this.items.filter((item) => {
            const matchesText = !query || [item.title, item.summary].join(' ').toLowerCase().includes(query);
            const matchesStatus = !this.statusFilter || item.status === this.statusFilter;
            return matchesText && matchesStatus;
        });
        return rows.slice().sort((a, b) => {
            const pinned = Number(b.isPinned === true) - Number(a.isPinned === true);
            if (pinned !== 0) return pinned;
            if (this.sort === 'title') return a.title.localeCompare(b.title, 'ko');
            const compared = String(a.updatedAt).localeCompare(String(b.updatedAt));
            return this.sort === 'oldest' ? compared : -compared;
        });
    }

    public get pageCount() {
        return Math.max(1, Math.ceil(this.filteredItems.length / this.pageSize));
    }

    public get pageNumbers() {
        return Array.from({ length: this.pageCount }, (_, index) => index + 1);
    }

    public get paginatedItems() {
        const start = (this.page - 1) * this.pageSize;
        return this.filteredItems.slice(start, start + this.pageSize);
    }

    public async resetPage() {
        this.page = 1;
        await this.service.render();
    }

    public async goToPage(page: number) {
        this.page = Math.min(Math.max(page, 1), this.pageCount);
        await this.service.render();
    }

    public categoryClass(key: ContentCategory) {
        return key === this.category
            ? 'inline-flex h-11 shrink-0 items-center border-b-2 border-emerald-700 px-2 text-[12px] font-bold text-emerald-800'
            : 'inline-flex h-11 shrink-0 items-center border-b-2 border-transparent px-2 text-[12px] font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-800';
    }

    public statusClass(status: string) {
        return status === '공개'
            ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
            : 'border-slate-200 bg-slate-100 text-slate-500';
    }

    public async changeCategory(category: ContentCategory) {
        if (this.saving) return;
        this.search = '';
        this.statusFilter = '';
        this.sort = 'latest';
        this.page = 1;
        await this.router.navigate(['/ip-management/member-content'], { queryParams: { category } });
    }

    public async openItem(item: ContentItem) {
        if (this.saving) return;
        await this.router.navigate(['/ip-management/member-content'], {
            queryParams: { category: this.category, item: item.id }
        });
    }

    public async newContent() {
        if (this.saving || this.loadError) return;
        await this.router.navigate(['/ip-management/member-content'], {
            queryParams: { category: this.category, item: 'new' }
        });
    }

    public async closeEditor() {
        if (this.saving) return;
        await this.router.navigate(['/ip-management/member-content'], {
            queryParams: { category: this.category }
        });
    }

    public async editContent() {
        if (!this.selected) return;
        this.draft = this.cloneContent(this.selected);
        this.editing = true;
        this.notice = '';
        await this.service.render();
    }

    public async cancelEdit() {
        if (this.saving) return;
        if (!this.selected) {
            await this.closeEditor();
            return;
        }
        this.draft = this.cloneContent(this.selected);
        this.pendingFiles = [];
        this.editing = false;
        await this.service.render();
    }

    public clearFilters() {
        this.search = '';
        this.statusFilter = '';
        this.sort = 'latest';
    }

    private emptyContent(): ContentItem {
        return {
            id: '',
            title: '',
            summary: '',
            status: '임시저장',
            isPinned: false,
            updatedAt: '',
            linkUrl: '',
            linkLabel: '바로가기',
            attachments: [],
            version: 0
        };
    }

    private cloneContent(item: ContentItem): ContentItem {
        return {
            ...item,
            isPinned: item.isPinned === true,
            linkUrl: item.linkUrl || '',
            linkLabel: item.linkLabel || '바로가기',
            attachments: [...(item.attachments || [])],
            version: Number(item.version || 0)
        };
    }

    private upsert(item: ContentItem) {
        const rows = this.content[this.category];
        const index = rows.findIndex((row) => row.id === item.id);
        if (index >= 0) rows[index] = item;
        else rows.unshift(item);
        rows.sort((a, b) =>
            Number(b.isPinned === true) - Number(a.isPinned === true)
            || String(b.updatedAt || '').localeCompare(String(a.updatedAt || ''))
        );
    }

    private savePayload(item: ContentItem, isPinned: boolean = item.isPinned) {
        return {
            id: item.id || '',
            title: item.title,
            summary: item.summary,
            status: item.status,
            isPinned,
            linkUrl: item.linkUrl || '',
            linkLabel: item.linkLabel || '바로가기',
            version: Number(item.version || 0)
        };
    }

    public async saveContent() {
        if (this.saving || this.loadError) return;
        if (!this.draft.title.trim() || !this.draft.summary.trim()) {
            await this.service.modal.error('제목과 내용을 입력해주세요.');
            return;
        }

        if (this.draft.title.trim().length > 200 || this.draft.summary.trim().length > 10000) {
            await this.service.modal.error('제목은 200자, 내용은 10,000자 이하로 입력해 주세요.');
            return;
        }
        const linkUrl = normalizeWebsite(this.draft.linkUrl || '');
        if (linkUrl === null) {
            await this.service.modal.error('바로가기 주소는 올바른 http 또는 https 주소로 입력해 주세요.');
            return;
        }
        this.draft.linkUrl = linkUrl;
        this.saving = true;
        this.uploadProgress = 0;
        await this.service.render();
        try {
            let saved: ContentItem = this.responseData(await wiz.call('save_content', {
                section: this.category,
                data: JSON.stringify(this.savePayload(this.draft))
            }));
            this.upsert(saved);

            const files = this.supportsAttachments ? [...this.pendingFiles] : [];
            for (let index = 0; index < files.length; index += 1) {
                const form = new FormData();
                form.append('section', this.category);
                form.append('content_id', saved.id);
                form.append('file', files[index]);
                const response: any = await this.service.file.upload(
                    wiz.url('upload_content_file'),
                    form,
                    async (percent: number) => {
                        this.uploadProgress = Math.round(((index + percent / 100) / files.length) * 100);
                        await this.service.render();
                    }
                );
                saved = this.responseData(response);
                this.upsert(saved);
            }

            this.selected = this.items.find((item) => item.id === saved.id) || saved;
            this.draft = this.cloneContent(this.selected);
            this.pendingFiles = [];
            this.editing = false;
            this.notice = this.activeCategory.label + ' 콘텐츠가 저장되었습니다.';
            await this.router.navigate(['/ip-management/member-content'], {
                queryParams: { category: this.category, item: saved.id },
                replaceUrl: true
            });
        } catch (error) {
            await this.service.modal.error(String(error?.message || '콘텐츠를 저장하지 못했습니다.'));
        } finally {
            this.saving = false;
            await this.service.render();
        }
    }

    public async togglePinned(item: ContentItem, event?: MouseEvent) {
        event?.stopPropagation();
        if (this.saving || this.loadError) return;
        this.saving = true;
        await this.service.render();
        try {
            const saved: ContentItem = this.responseData(await wiz.call('save_content', {
                section: this.category,
                data: JSON.stringify(this.savePayload(item, !item.isPinned))
            }));
            this.upsert(saved);
            if (this.selected?.id === saved.id) {
                this.selected = saved;
                this.draft = this.cloneContent(saved);
            }
            this.notice = saved.isPinned
                ? '★ 상단 고정을 설정했습니다.'
                : '상단 고정을 해제했습니다.';
        } catch (error) {
            await this.service.modal.error(String(error?.message || '상단 고정 상태를 변경하지 못했습니다.'));
        } finally {
            this.saving = false;
            await this.service.render();
        }
    }

    public async deleteContent() {
        if (!this.selected || this.saving || this.editing) return;
        const confirmed = await this.service.modal.show({
            title: this.activeCategory.label + ' 삭제',
        message: '“' + this.selected.title + '” 항목을 삭제하시겠습니까?\n삭제 후에는 복구할 수 없습니다.',
            action: '삭제',
            cancel: '취소',
            status: 'error',
            actionBtn: 'error'
        });
        if (!confirmed) return;

        this.saving = true;
        await this.service.render();
        try {
            const deleted: any = this.responseData(await wiz.call('delete_content', {
                section: this.category,
                content_id: this.selected.id,
                version: Number(this.selected.version || 0)
            }));
            const index = this.items.findIndex((item) => item.id === deleted.id);
            if (index >= 0) this.items.splice(index, 1);
            this.selected = null;
            this.editorOpen = false;
            this.editing = false;
            this.notice = this.activeCategory.label + ' 콘텐츠가 삭제되었습니다.';
            await this.router.navigate(['/ip-management/member-content'], {
                queryParams: { category: this.category },
                replaceUrl: true
            });
        } catch (error) {
            await this.service.modal.error(String(error?.message || '콘텐츠를 삭제하지 못했습니다.'));
        } finally {
            this.saving = false;
            await this.service.render();
        }
    }

    public async onFilesSelected(event: any) {
        if (!this.supportsAttachments) {
            if (event?.target) event.target.value = '';
            await this.service.modal.error('CoreAPI 콘텐츠는 현재 첨부파일을 지원하지 않습니다.');
            return;
        }
        const selected = Array.from(event?.target?.files || []) as File[];
        const accepted: File[] = [];
        const rejected: string[] = [];
        let count = (this.draft.attachments || []).length + this.pendingFiles.length;

        for (const file of selected) {
            const dot = file.name.lastIndexOf('.');
            const extension = dot >= 0 ? file.name.slice(dot).toLowerCase() : '';
            if (!this.allowedExtensions.includes(extension)) {
                rejected.push(file.name + ': 지원하지 않는 형식');
                continue;
            }
            if (file.size > this.maxFileSize) {
                rejected.push(file.name + ': 20MB 초과');
                continue;
            }
            if (count >= this.maxFiles) {
                rejected.push(file.name + ': 최대 10개 초과');
                continue;
            }
            accepted.push(file);
            count += 1;
        }

        this.pendingFiles = [...this.pendingFiles, ...accepted];
        if (event?.target) event.target.value = '';
        if (rejected.length) await this.service.modal.error(rejected.join('\n'));
        await this.service.render();
    }

    public async removePendingFile(index: number) {
        this.pendingFiles.splice(index, 1);
        await this.service.render();
    }

    public async deleteAttachment(file: any) {
        if (!this.draft.id || this.saving) return;
        this.saving = true;
        await this.service.render();
        try {
            const saved: ContentItem = this.responseData(await wiz.call('delete_content_file', {
                section: this.category,
                content_id: this.draft.id,
                attachment_id: file.id
            }));
            this.upsert(saved);
            this.selected = saved;
            this.draft = this.cloneContent(saved);
        } catch (error) {
            await this.service.modal.error(String(error?.message || '첨부파일을 삭제하지 못했습니다.'));
        } finally {
            this.saving = false;
            await this.service.render();
        }
    }

    public downloadAttachment(file: any) {
        if (!this.draft.id || !file?.id) return;
        const query = '?section=' + encodeURIComponent(this.category)
            + '&content_id=' + encodeURIComponent(this.draft.id)
            + '&attachment_id=' + encodeURIComponent(file.id);
        window.open(wiz.url('download_content_file') + query, '_blank', 'noopener,noreferrer');
    }

    public openLink(url: string) {
        if (!url) return;
        window.open(url.indexOf('://') >= 0 ? url : 'https://' + url, '_blank', 'noopener,noreferrer');
    }

    public formatFileSize(size: number) {
        const bytes = Number(size || 0);
        if (bytes < 1024) return bytes + ' B';
        if (bytes < 1024 * 1024) return Math.round(bytes / 102.4) / 10 + ' KB';
        return Math.round(bytes / 1024 / 102.4) / 10 + ' MB';
    }
}

export default PageIpManagementMember_contentComponent;